// Ticket rules: numbering, service day, order state machine, timers. Pure functions, no I/O.
// Order state (waiting, ready, collected...) and message delivery state are separate machines;
// delivery state lives in notify.ts.

export type TicketStatus = 'waiting' | 'ready' | 'collected' | 'expired' | 'abandoned' | 'cancelled';
export type NumberMode = 'scan' | 'ticket';
export type Channel = 'whatsapp' | 'web' | 'push' | 'sms' | 'none';
export type Lang = 'fr' | 'ar' | 'en';

import type { CallState } from './call';

export interface TicketState {
  id: string;
  pickupPointId: string;
  venueId: string;
  serviceDate: string;
  displayNo: string;
  mode: NumberMode;
  status: TicketStatus;
  channel: Channel;
  phone: string | null;
  lang: Lang;
  token: string;
  clientRef: string | null;
  deviceKey: string | null;
  waWindowUntil: number | null;
  createdAt: number;
  readyAt: number | null;
  collectedAt: number | null;
  closedAt: number | null;
  resends: number;
  lastResendAt: number | null;
  /** True when no channel can reach the guest: staff must call the number out loud. */
  needsCall: boolean;
  /** The WhatsApp call to the guest (issue #95): the automatic one 5 minutes after ready, or the latest attempt. */
  call?: CallState | null;
}

export const TIMERS = {
  smsFallbackMs: 20_000,
  expireAfterReadyMs: 30 * 60_000,
  abandonAfterWaitingMs: 3 * 60 * 60_000,
  feedbackAfterCollectedMs: 15 * 60_000,
  waWindowMs: 24 * 60 * 60_000,
  resendCooldownMs: 60_000,
  maxResends: 2,
  phoneRetentionMs: 30 * 24 * 60 * 60_000,
  feedbackCapMs: 30 * 24 * 60 * 60_000,
} as const;

export const OPEN_STATUSES: readonly TicketStatus[] = ['waiting', 'ready'];
export const MAX_DISPLAY_NO = 999;
export const MAX_RECEIPT_DIGITS = 6;
export const MAX_OPEN_PER_DEVICE = 3;
export const SUPPORTED_LANGS: readonly Lang[] = ['fr', 'ar', 'en'];

export class TicketError extends Error {
  constructor(public readonly code: string, message?: string) {
    super(message ?? code);
    this.name = 'TicketError';
  }
}

export function isOpen(t: Pick<TicketState, 'status'>): boolean {
  return OPEN_STATUSES.includes(t.status);
}

/** What one device holds at a pickup point today, and what a rescan may do with it. */
export interface DeviceTickets<T> {
  /** The device's open tickets, oldest first. */
  mine: T[];
  /** Where a plain rescan lands: the most recent open ticket, or null when the device has none. */
  latest: T | null;
  /** Whether an explicit "another order" may issue one more. */
  canAdd: boolean;
}

/**
 * Open tickets held by one device. A plain rescan returns `latest` instead of issuing a number;
 * only an explicit "another order" issues one more, and never past `max` (3) open tickets.
 * Without a device key (POS or staff tickets) there is nothing to list and no limit.
 */
export function deviceOpenTickets<T extends { deviceKey: string | null; createdAt: number }>(
  open: Iterable<T>,
  deviceKey: string | null | undefined,
  max: number = MAX_OPEN_PER_DEVICE,
): DeviceTickets<T> {
  if (!deviceKey) return { mine: [], latest: null, canAdd: true };
  const mine = [...open].filter((o) => o.deviceKey === deviceKey).sort((a, b) => a.createdAt - b.createdAt);
  return { mine, latest: mine.length > 0 ? mine[mine.length - 1] : null, canAdd: mine.length < max };
}

/** Next free number after `lastNo`, wrapping at 999 to `firstNo`, skipping numbers still open today. */
export function nextDisplayNo(lastNo: number, firstNo: number, open: ReadonlySet<string>): number {
  let n = lastNo;
  for (let i = 0; i <= MAX_DISPLAY_NO; i++) {
    n = n < firstNo || n >= MAX_DISPLAY_NO ? firstNo : n + 1;
    if (!open.has(String(n))) return n;
  }
  throw new TicketError('no_free_number');
}

/** Receipt number typed by the guest or sent by a POS: 1 to 6 digits, leading zeros dropped. */
export function parseReceiptNo(input: string): string | null {
  const digits = input.trim().replace(/\D/g, '');
  if (digits.length === 0 || digits.length > MAX_RECEIPT_DIGITS) return null;
  return digits.replace(/^0+(?=\d)/, '');
}

// ---- the service day ----
// One definition for everything that depends on the day: numbering, the board, the rollover alarm and the timers.
// `serviceDateFor` says which service day an instant belongs to; every boundary below is derived from it, so the
// rollover and the timer deadlines can never disagree with the date stamped on a ticket.

/** Day start of a venue that never set one: 05:00, so a counter open past midnight keeps one sequence. */
export const DEFAULT_DAY_START_HOUR = 5;
/** Every venue runs on Moroccan time. */
export const SERVICE_TIME_ZONE = 'Africa/Casablanca';

const HOUR_MS = 60 * 60_000;
const SERVICE_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const dayFormats = new Map<string, Intl.DateTimeFormat>();

function dayFormat(timeZone: string): Intl.DateTimeFormat {
  let f = dayFormats.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hour12: false });
    dayFormats.set(timeZone, f);
  }
  return f;
}

/** A venue's day start hour as stored: a whole hour from 0 to 23; anything else falls back to 05:00. */
export function dayStartHourOf(x: unknown): number {
  return typeof x === 'number' && Number.isInteger(x) && x >= 0 && x <= 23 ? x : DEFAULT_DAY_START_HOUR;
}

/** True for a "YYYY-MM-DD" service date. */
export function isServiceDate(x: unknown): x is string {
  return typeof x === 'string' && SERVICE_DATE_RE.test(x);
}

/** Service day in Morocco, starting at `dayStartHour` (default 05:00): 04:59 still belongs to yesterday. */
export function serviceDateFor(now: Date | number, dayStartHour = DEFAULT_DAY_START_HOUR, timeZone = SERVICE_TIME_ZONE): string {
  const parts: Record<string, string> = {};
  for (const p of dayFormat(timeZone).formatToParts(typeof now === 'number' ? now : now.getTime())) parts[p.type] = p.value;
  let y = Number(parts.year);
  let m = Number(parts.month);
  let d = Number(parts.day);
  const h = Number(parts.hour) % 24;
  if (h < dayStartHourOf(dayStartHour)) {
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() - 1);
    y = dt.getUTCFullYear();
    m = dt.getUTCMonth() + 1;
    d = dt.getUTCDate();
  }
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** The service date `days` after (negative: before) `serviceDate`. */
export function addServiceDays(serviceDate: string, days: number): string {
  const m = SERVICE_DATE_RE.exec(serviceDate);
  // A malformed stored date is a bug, not a refusal a screen could word: it stays out of the API error codes (a 500).
  if (!m) throw new RangeError('bad_service_date');
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days)).toISOString().slice(0, 10);
}

/**
 * The first instant (epoch ms) of service day `serviceDate`: its day start hour, local time. Found by bisecting
 * `serviceDateFor` itself rather than by computing the zone offset, so it follows whatever time zone data the
 * runtime has (Morocco moves its clock around Ramadan) and always agrees with the date stamped on tickets.
 */
export function serviceDayStart(serviceDate: string, dayStartHour = DEFAULT_DAY_START_HOUR, timeZone = SERVICE_TIME_ZONE): number {
  const m = SERVICE_DATE_RE.exec(serviceDate);
  // A malformed stored date is a bug, not a refusal a screen could word: it stays out of the API error codes (a 500).
  if (!m) throw new RangeError('bad_service_date');
  const hour = dayStartHourOf(dayStartHour);
  // The day start read as if it were UTC. Zones sit between UTC-12 and UTC+14, so the real instant lies in
  // (wall - 15 h, wall + 13 h]: at the low end the service date is still the previous one, at the high end it is this one.
  const wall = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), hour);
  let before = wall - 15 * HOUR_MS;
  let after = wall + 13 * HOUR_MS;
  while (after - before > 1) {
    const mid = Math.floor((before + after) / 2);
    if (serviceDateFor(mid, hour, timeZone) >= serviceDate) after = mid;
    else before = mid;
  }
  return after;
}

/** The instant service day `serviceDate` ends: the next day's start. Leftover orders close here. */
export function serviceDayEnd(serviceDate: string, dayStartHour = DEFAULT_DAY_START_HOUR, timeZone = SERVICE_TIME_ZONE): number {
  return serviceDayStart(addServiceDays(serviceDate, 1), dayStartHour, timeZone);
}

/** When the service day running at `now` ends: the time of the pickup point's rollover alarm. */
export function nextServiceDayStart(now: number, dayStartHour = DEFAULT_DAY_START_HOUR, timeZone = SERVICE_TIME_ZONE): number {
  return serviceDayEnd(serviceDateFor(now, dayStartHour, timeZone), dayStartHour, timeZone);
}

/**
 * The service date a pickup point kept on `stored` must move to at `now`, or null while `stored` is still current.
 * Only forward: moving the day start later never sends a board back to yesterday. An empty `stored` (a pickup point
 * that never issued a number) always rolls.
 */
export function serviceDayRollover(stored: string, now: number, dayStartHour = DEFAULT_DAY_START_HOUR, timeZone = SERVICE_TIME_ZONE): string | null {
  const current = serviceDateFor(now, dayStartHour, timeZone);
  return current > stored ? current : null;
}

/**
 * How an order still open when its service day ends is closed: a ready order expires (a no-show), a waiting one is
 * abandoned (left out of alert metrics), the same transitions as the 30-minute and 3-hour timers. Null once closed.
 */
export function dayEndClosing(t: Pick<TicketState, 'status'>): 'expire' | 'abandon' | null {
  if (t.status === 'ready') return 'expire';
  if (t.status === 'waiting') return 'abandon';
  return null;
}

/** True for one of the three interface languages, as a request body or a column may spell it. */
export function isLang(x: unknown): x is Lang {
  return typeof x === 'string' && (SUPPORTED_LANGS as readonly string[]).includes(x);
}

export function pickLang(requested: string | null | undefined, fallback: Lang): Lang {
  const l = (requested ?? '').toLowerCase().slice(0, 2);
  return (SUPPORTED_LANGS as readonly string[]).includes(l) ? (l as Lang) : fallback;
}

// ---- transitions ----

export function markReady(t: TicketState, now: number): TicketState {
  if (t.status !== 'waiting') throw new TicketError('not_waiting');
  return { ...t, status: 'ready', readyAt: now };
}

/**
 * Staff tap "Retirée" or the POS calls `/collected` (US-36). Only a ready order can be collected (spec, "Statuses"):
 * a waiting one is refused with `not_ready` (it was never called; staff mark it ready first, or cancel it), a closed
 * one with `not_open`, so a double tap or a second device cannot collect twice or emit `ticket.collected` twice.
 */
export function markCollected(t: TicketState, now: number): TicketState {
  if (t.status === 'waiting') throw new TicketError('not_ready');
  if (t.status !== 'ready') throw new TicketError('not_open');
  return { ...t, status: 'collected', collectedAt: now, closedAt: now };
}

export function cancelTicket(t: TicketState, now: number): TicketState {
  if (!isOpen(t)) throw new TicketError('not_open');
  return { ...t, status: 'cancelled', closedAt: now };
}

export function expireTicket(t: TicketState, now: number): TicketState {
  if (t.status !== 'ready') throw new TicketError('not_ready');
  return { ...t, status: 'expired', closedAt: now };
}

export function abandonTicket(t: TicketState, now: number): TicketState {
  if (t.status !== 'waiting') throw new TicketError('not_waiting');
  return { ...t, status: 'abandoned', closedAt: now };
}

export function attachChannel(t: TicketState, channel: Channel, phone: string | null, now: number): TicketState {
  if (!isOpen(t)) throw new TicketError('not_open');
  return {
    ...t,
    channel,
    phone: phone ?? t.phone,
    needsCall: false,
    waWindowUntil: channel === 'whatsapp' ? now + TIMERS.waWindowMs : t.waWindowUntil,
  };
}

/** A kiosk-created ticket has no device until a phone opens it with a live scan token; that phone becomes the issuing device. */
export function bindDevice(t: TicketState, deviceKey: string): TicketState {
  if (t.deviceKey) return t;
  return { ...t, deviceKey };
}

/** A wrong number can be corrected only while the order is still in preparation: once ready, the number has been alerted. */
export function canCorrectNumber(t: Pick<TicketState, 'status'>): boolean {
  return t.status === 'waiting';
}

/**
 * Staff correct a wrong number (issue #17). Same parsing as the diner's receipt form; refused once the ticket is
 * ready. Uniqueness among today's open tickets is the pickup point's and D1's business (`numberOpenElsewhere`).
 */
export function correctDisplayNo(t: TicketState, displayNo: string): TicketState {
  if (!canCorrectNumber(t)) throw new TicketError('not_waiting');
  const parsed = parseReceiptNo(displayNo);
  if (!parsed) throw new TicketError('bad_number');
  return { ...t, displayNo: parsed };
}

/** True when another open ticket at the pickup point already shows this number today (the ticket itself does not count). */
export function numberOpenElsewhere<T extends { id: string; displayNo: string }>(open: Iterable<T>, displayNo: string, ticketId: string): boolean {
  for (const o of open) if (o.id !== ticketId && o.displayNo === displayNo) return true;
  return false;
}

export function isWindowOpen(t: Pick<TicketState, 'waWindowUntil'>, now: number): boolean {
  return t.waWindowUntil !== null && now < t.waWindowUntil;
}

export function canResend(t: TicketState, now: number): { ok: true } | { ok: false; reason: 'not_ready' | 'limit' | 'cooldown' } {
  if (t.status !== 'ready') return { ok: false, reason: 'not_ready' };
  if (t.resends >= TIMERS.maxResends) return { ok: false, reason: 'limit' };
  if (t.lastResendAt !== null && now - t.lastResendAt < TIMERS.resendCooldownMs) return { ok: false, reason: 'cooldown' };
  return { ok: true };
}

export function recordResend(t: TicketState, now: number): TicketState {
  const check = canResend(t, now);
  if (!check.ok) throw new TicketError(`resend_${check.reason}`);
  return { ...t, resends: t.resends + 1, lastResendAt: now };
}

/**
/**
 * When the feedback question is due once an order is collected (US-36, spec "Feedback"): 15 minutes after
 * `collectedAt` (owner decision, 16 Oct 2026; `delayMs` 0 for the POC demo venue, `feedbackDelayFor`), only for a WhatsApp guest with a phone whose 24-hour window is still open at that moment, so the
 * question stays a free service message. Null means no feedback timer (web page, SMS-only or unregistered guests,
 * or a window that closes first). The once-per-30-days cap per phone and venue is checked when the question is sent.
 */
export function feedbackDeadline(t: TicketState, delayMs: number = TIMERS.feedbackAfterCollectedMs): number | null {
  if (t.status !== 'collected' || t.collectedAt === null) return null;
  if (t.channel !== 'whatsapp' || !t.phone) return null;
  const at = t.collectedAt + delayMs;
  return isWindowOpen(t, at) ? at : null;
}

/**
 * Timer deadlines implied by the current state. Null means no timer of that kind. An order never outlives its
 * service day: the abandon and expiry deadlines are capped at `dayEnd` (`serviceDayEnd` of the ticket's date, the
 * same boundary as the pickup point's rollover), so a leftover closes at the venue day start without a new scan.
 * The feedback question is not capped: it follows a collected order, which is already closed.
 */
export function timerDeadlines(
  t: TicketState,
  dayStartHour = DEFAULT_DAY_START_HOUR,
  timeZone = SERVICE_TIME_ZONE,
): { abandon: number | null; expire: number | null; feedback: number | null; dayEnd: number | null } {
  const dayEnd = isServiceDate(t.serviceDate) ? serviceDayEnd(t.serviceDate, dayStartHour, timeZone) : null;
  const capped = (at: number) => (dayEnd === null ? at : Math.min(at, dayEnd));
  return {
    abandon: t.status === 'waiting' ? capped(t.createdAt + TIMERS.abandonAfterWaitingMs) : null,
    expire: t.status === 'ready' && t.readyAt !== null ? capped(t.readyAt + TIMERS.expireAfterReadyMs) : null,
    feedback: feedbackDeadline(t),
    dayEnd,
  };
}
